// Shapefile loader module with drag-and-drop support

import shp from 'shpjs';
import JSZip from 'jszip';
import proj4 from 'proj4';

// Define common projections for reprojection
proj4.defs('EPSG:3857', '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs');

/**
 * Detect if files contain shapefile components and group them
 * @param {Array<File>} files - Array of files
 * @returns {Object|null} Grouped shapefile data or null
 */
export function detectShapefileComponents(files) {
    console.log('detectShapefileComponents called with files:', files.map(f => f.name));

    // Find .shp file
    const shpFile = files.find(f => f.name.toLowerCase().endsWith('.shp'));

    if (!shpFile) {
        console.log('No .shp file found');
        return null;
    }

    // Get base name (without extension)
    const baseName = shpFile.name.replace(/\.shp$/i, '');
    console.log('Found .shp file with base name:', baseName);

    // Find companion files with same base name
    const components = {
        shp: shpFile,
        dbf: null,
        shx: null,
        prj: null,
        cpg: null
    };

    files.forEach(file => {
        const fileName = file.name;
        const fileBaseName = fileName.replace(/\.[^.]+$/, '');

        // Check if this file belongs to the same shapefile
        if (fileBaseName.toLowerCase() === baseName.toLowerCase()) {
            const ext = fileName.split('.').pop().toLowerCase();
            if (ext === 'dbf') components.dbf = file;
            else if (ext === 'shx') components.shx = file;
            else if (ext === 'prj') components.prj = file;
            else if (ext === 'cpg') components.cpg = file;
        }
    });

    // Validate required components
    console.log('Found components:', {
        shp: !!components.shp,
        dbf: !!components.dbf,
        shx: !!components.shx,
        prj: !!components.prj,
        cpg: !!components.cpg
    });

    if (!components.dbf || !components.shx) {
        const missing = [];
        if (!components.dbf) missing.push('.dbf');
        if (!components.shx) missing.push('.shx');
        console.log('Missing required components:', missing);
        throw new Error(`Missing required shapefile components: ${missing.join(', ')}`);
    }

    console.log('All required components found!');
    return {
        baseName,
        components
    };
}

/**
 * Create a virtual zip file from shapefile components
 * @param {Object} shapefileData - Grouped shapefile data from detectShapefileComponents
 * @returns {Promise<File>} Virtual zip file
 */
export async function createVirtualZip(shapefileData) {
    const { baseName, components } = shapefileData;
    const zip = new JSZip();

    // Add all components to the zip
    for (const [ext, file] of Object.entries(components)) {
        if (file) {
            const arrayBuffer = await file.arrayBuffer();
            zip.file(`${baseName}.${ext}`, arrayBuffer);
        }
    }

    // Generate zip file
    const zipBlob = await zip.generateAsync({ type: 'blob' });

    // Create File object from blob
    return new File([zipBlob], `${baseName}.zip`, { type: 'application/zip' });
}

/**
 * Calculate bounds from GeoJSON features
 * @param {Object} geojson - GeoJSON FeatureCollection
 * @returns {Object|null} Bounds object with minLon, maxLon, minLat, maxLat
 */
function calculateBounds(geojson) {
    const features = geojson.features || [];
    if (features.length === 0) return null;

    const coords = [];
    features.forEach(feature => {
        extractCoordinates(feature.geometry, coords);
    });

    if (coords.length === 0) return null;

    let minLon = Infinity, maxLon = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    coords.forEach(coord => {
        if (coord && coord.length >= 2) {
            const lon = coord[0];
            const lat = coord[1];
            if (lon < minLon) minLon = lon;
            if (lon > maxLon) maxLon = lon;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
        }
    });

    return { minLon, maxLon, minLat, maxLat };
}

/**
 * Load and parse a shapefile from a file
 * @param {File} file - The shapefile zip file
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} Parsed GeoJSON data
 */
export async function loadShapefile(file, onProgress) {
    try {
        // Read file as ArrayBuffer
        const arrayBuffer = await readFileAsArrayBuffer(file, onProgress);

        // Extract raw .prj file content before shpjs processes it
        let rawPrj = null;
        try {
            const zip = await JSZip.loadAsync(arrayBuffer);
            // Look for .prj file (case insensitive)
            const prjFile = Object.keys(zip.files).find(name => name.toLowerCase().endsWith('.prj'));
            if (prjFile) {
                rawPrj = await zip.files[prjFile].async('string');
                console.log('Extracted raw .prj file:', rawPrj);
            } else {
                console.log('No .prj file found in shapefile');
            }
        } catch (error) {
            console.warn('Failed to extract .prj file:', error);
        }

        // Parse shapefile using shpjs
        const geojson = await shp(arrayBuffer);
        console.log('=== SHAPEFILE LOAD DEBUG ===');
        console.log('Raw parsed data type:', typeof geojson, geojson?.type);
        console.log('Raw parsed data:', geojson);

        // Normalize to FeatureCollection if needed
        const normalized = normalizeGeoJSON(geojson);
        console.log('After normalization - features:', normalized.features?.length || 0);

        // Preserve the raw prj content
        if (rawPrj) {
            normalized.prj = rawPrj;
        }

        // Calculate bounds in ORIGINAL projection before reprojection
        const originalBounds = calculateBounds(normalized);

        // Auto-reproject to WGS84 if projection is detected
        const { geojson: reprojectedGeoJSON, projectionInfo } = await reprojectToWGS84(normalized, rawPrj);
        console.log('After reprojection - features:', reprojectedGeoJSON.features?.length || 0);

        // Store original bounds in the reprojected GeoJSON
        if (originalBounds && projectionInfo.reprojected) {
            reprojectedGeoJSON.originalBounds = originalBounds;
        }

        console.log('=== FINAL LOAD RESULT ===');
        console.log('Total features loaded:', reprojectedGeoJSON.features?.length || 0);

        return {
            geojson: reprojectedGeoJSON,
            fileName: file.name,
            fileSize: file.size,
            featureCount: reprojectedGeoJSON.features?.length || 0,
            projectionInfo
        };
    } catch (error) {
        console.error('Error loading shapefile:', error);
        throw new Error(`Failed to load shapefile: ${error.message}`);
    }
}

/**
 * Reproject GeoJSON coordinates to WGS84
 * @param {Object} geojson - The GeoJSON FeatureCollection
 * @param {string} prjWKT - WKT projection string from .prj file
 * @returns {Promise<Object>} Reprojected GeoJSON and projection info
 */
async function reprojectToWGS84(geojson, prjWKT) {
    const WGS84 = 'EPSG:4326';

    // If no projection info, assume WGS84
    if (!prjWKT) {
        console.log('No projection info found, assuming WGS84');
        return {
            geojson,
            projectionInfo: {
                source: 'WGS84 (assumed)',
                target: 'WGS84',
                reprojected: false
            }
        };
    }

    try {
        // Check if data is already in WGS84
        const isWGS84 = prjWKT.includes('GCS_WGS_1984') ||
                        prjWKT.includes('GEOGCS["WGS 84"') ||
                        prjWKT.includes('EPSG","4326');

        if (isWGS84) {
            console.log('Data is already in WGS84, no reprojection needed');
            return {
                geojson,
                projectionInfo: {
                    source: 'WGS84',
                    target: 'WGS84',
                    reprojected: false
                }
            };
        }

        // Check if WKT contains an EPSG code we can use directly
        const epsgMatch = prjWKT.match(/AUTHORITY\["EPSG","(\d+)"\]/);
        let sourceProjection;
        let sourceName;

        if (epsgMatch) {
            // Use EPSG code directly (e.g., EPSG:3857)
            const epsgCode = epsgMatch[1];
            sourceProjection = `EPSG:${epsgCode}`;

            // Extract projection name for display
            const nameMatch = prjWKT.match(/PROJCS\["([^"]+)"\]|GEOGCS\["([^"]+)"\]/);
            sourceName = nameMatch ? (nameMatch[1] || nameMatch[2]) : `EPSG:${epsgCode}`;

            console.log(`Using EPSG:${epsgCode} for reprojection`);
        } else {
            // Define source projection from WKT
            proj4.defs('SOURCE_PRJ', prjWKT);
            sourceProjection = 'SOURCE_PRJ';

            // Extract projection name for display
            const nameMatch = prjWKT.match(/PROJCS\["([^"]+)"\]|GEOGCS\["([^"]+)"\]/);
            sourceName = nameMatch ? (nameMatch[1] || nameMatch[2]) : 'Custom Projection';

            console.log(`Defined custom projection from WKT`);
        }

        console.log(`Reprojecting from ${sourceName} to WGS84`);

        // Create transformation function
        const transform = proj4(sourceProjection, WGS84);

        // Clone GeoJSON to avoid modifying original
        const reprojected = JSON.parse(JSON.stringify(geojson));

        // Transform all coordinates
        reprojected.features.forEach(feature => {
            if (feature.geometry) {
                transformGeometryCoordinates(feature.geometry, transform);
            }
        });

        console.log('Reprojection complete');

        return {
            geojson: reprojected,
            projectionInfo: {
                source: sourceName,
                target: 'WGS84',
                reprojected: true
            }
        };
    } catch (error) {
        console.warn('Failed to reproject, using original coordinates:', error);
        return {
            geojson,
            projectionInfo: {
                source: 'Unknown (reprojection failed)',
                target: 'WGS84',
                reprojected: false,
                error: error.message
            }
        };
    }
}

/**
 * Transform geometry coordinates recursively
 * @param {Object} geometry - GeoJSON geometry
 * @param {Function} transform - proj4 transform function
 */
function transformGeometryCoordinates(geometry, transform) {
    if (!geometry || !geometry.type) return;

    switch (geometry.type) {
        case 'Point':
            geometry.coordinates = transform.forward(geometry.coordinates);
            break;
        case 'MultiPoint':
        case 'LineString':
            geometry.coordinates = geometry.coordinates.map(coord => transform.forward(coord));
            break;
        case 'MultiLineString':
        case 'Polygon':
            geometry.coordinates = geometry.coordinates.map(ring =>
                ring.map(coord => transform.forward(coord))
            );
            break;
        case 'MultiPolygon':
            geometry.coordinates = geometry.coordinates.map(polygon =>
                polygon.map(ring =>
                    ring.map(coord => transform.forward(coord))
                )
            );
            break;
        case 'GeometryCollection':
            geometry.geometries.forEach(geom => transformGeometryCoordinates(geom, transform));
            break;
    }
}

/**
 * Read file as ArrayBuffer with progress tracking
 * @param {File} file - The file to read
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<ArrayBuffer>}
 */
function readFileAsArrayBuffer(file, onProgress) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onprogress = (event) => {
            if (event.lengthComputable && onProgress) {
                const progress = (event.loaded / event.total) * 100;
                onProgress(progress, 'Reading file...');
            }
        };

        reader.onload = (event) => {
            resolve(event.target.result);
        };

        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };

        reader.readAsArrayBuffer(file);
    });
}

/**
 * Normalize GeoJSON to FeatureCollection format
 * @param {Object} geojson - Raw GeoJSON data
 * @returns {Object} Normalized FeatureCollection
 */
function normalizeGeoJSON(geojson) {
    // If it's already a FeatureCollection, return as is
    if (geojson.type === 'FeatureCollection') {
        return geojson;
    }

    // If it's an array of FeatureCollections, merge them
    if (Array.isArray(geojson)) {
        const allFeatures = geojson.flatMap(item => {
            if (item.type === 'FeatureCollection') {
                return item.features;
            } else if (item.type === 'Feature') {
                return [item];
            }
            return [];
        });

        return {
            type: 'FeatureCollection',
            features: allFeatures
        };
    }

    // If it's a single Feature, wrap it
    if (geojson.type === 'Feature') {
        return {
            type: 'FeatureCollection',
            features: [geojson]
        };
    }

    // If it's a Geometry, wrap it in a Feature and FeatureCollection
    if (geojson.type && geojson.coordinates) {
        return {
            type: 'FeatureCollection',
            features: [{
                type: 'Feature',
                geometry: geojson,
                properties: {}
            }]
        };
    }

    // Fallback
    return geojson;
}

/**
 * Setup drag and drop handlers for an element
 * @param {HTMLElement} element - The element to attach handlers to
 * @param {Function} onDrop - Callback when file is dropped
 */
export function setupDragAndDrop(element, onDrop) {
    // Prevent default drag behaviors
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        element.addEventListener(eventName, preventDefaults, false);
        document.body.addEventListener(eventName, preventDefaults, false);
    });

    // Highlight drop area
    ['dragenter', 'dragover'].forEach(eventName => {
        element.addEventListener(eventName, () => {
            element.classList.add('drag-over');
        }, false);
    });

    ['dragleave', 'drop'].forEach(eventName => {
        element.addEventListener(eventName, () => {
            element.classList.remove('drag-over');
        }, false);
    });

    // Handle dropped files
    element.addEventListener('drop', async (e) => {
        const items = Array.from(e.dataTransfer.items);

        // Check if any item is a directory
        const hasDirectory = items.some(item =>
            item.webkitGetAsEntry && item.webkitGetAsEntry()?.isDirectory
        );

        if (hasDirectory) {
            // Handle folder drop
            const allFiles = [];

            for (const item of items) {
                const entry = item.webkitGetAsEntry();
                if (entry) {
                    const files = await readDirectoryRecursive(entry);
                    allFiles.push(...files);
                }
            }

            if (allFiles.length === 0) {
                if (window.showAlert) {
                    await window.showAlert('No Files Found', 'No valid files found in the dropped folder.');
                } else {
                    alert('No valid files found in folder');
                }
                return;
            }

            // Check for shapefile components first
            const hasShapefile = allFiles.some(f => f.name.toLowerCase().endsWith('.shp'));

            if (hasShapefile) {
                console.log('Found shapefile in folder, passing all files for detection');
                // Pass all files so shapefile detection can find components
                onDrop(allFiles);
                return;
            }

            // Otherwise check for images
            const imageExtensions = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
            const imageFiles = allFiles.filter(file => {
                const fileName = file.name.toLowerCase();
                return imageExtensions.some(ext => fileName.endsWith(ext));
            });

            if (imageFiles.length === 0) {
                if (window.showAlert) {
                    await window.showAlert('No Valid Files Found', 'No shapefiles or photos found in the dropped folder.');
                } else {
                    alert('No valid files found in folder');
                }
                return;
            }

            // Pass all image files
            onDrop(imageFiles);
            return;
        }

        // Handle regular file drop
        const files = Array.from(e.dataTransfer.files);
        if (files.length === 0) return;

        const validExtensions = ['.zip', '.shp', '.dbf', '.shx', '.prj', '.cpg', '.csv', '.txt', '.racas', '.xls', '.xlsx', '.json', '.geojson', '.kml', '.gpx', '.jpg', '.jpeg', '.png', '.heic', '.heif'];

        // Filter valid files
        const validFiles = files.filter(file => {
            const fileName = file.name.toLowerCase();
            return validExtensions.some(ext => fileName.endsWith(ext));
        });

        if (validFiles.length === 0) {
            if (window.showAlert) {
                await window.showAlert('Invalid File Type', 'Please drop valid files: Shapefile (.zip), CSV, Excel (.xls/.xlsx), GeoJSON, KML, GPX, or Photos (JPG/PNG/HEIC).');
            } else {
                alert('Please drop valid files: Shapefile (.zip), CSV, Excel (.xls/.xlsx), GeoJSON, KML, GPX, or Photos (JPG/PNG/HEIC)');
            }
            return;
        }

        // If single file, pass it directly for backward compatibility
        // If multiple files, pass array
        if (validFiles.length === 1) {
            onDrop(validFiles[0]);
        } else {
            onDrop(validFiles);
        }
    }, false);
}

/**
 * Prevent default drag behaviors
 * @param {Event} e - The event
 */
function preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
}

/**
 * Read directory contents recursively
 * @param {FileSystemEntry} entry - Directory entry
 * @returns {Promise<Array<File>>} Array of files
 */
async function readDirectoryRecursive(entry) {
    const files = [];

    if (entry.isFile) {
        // Get file from entry
        const file = await new Promise((resolve, reject) => {
            entry.file(resolve, reject);
        });
        return [file];
    }

    if (entry.isDirectory) {
        const dirReader = entry.createReader();
        const entries = await new Promise((resolve, reject) => {
            dirReader.readEntries(resolve, reject);
        });

        for (const childEntry of entries) {
            const childFiles = await readDirectoryRecursive(childEntry);
            files.push(...childFiles);
        }
    }

    return files;
}

/**
 * Extract metadata from GeoJSON
 * @param {Object} geojson - The GeoJSON data
 * @returns {Object} Metadata
 */
export function extractMetadata(geojson) {
    const features = geojson.features || [];
    const featureCount = features.length;

    // Get geometry types
    const geometryTypes = [...new Set(
        features.map(f => f.geometry?.type).filter(Boolean)
    )];

    // Get property keys
    const propertyKeys = features.length > 0
        ? Object.keys(features[0].properties || {})
        : [];

    // Calculate bounds
    let bounds = null;
    if (features.length > 0) {
        const coords = [];
        features.forEach(feature => {
            extractCoordinates(feature.geometry, coords);
        });

        if (coords.length > 0) {
            // Use reduce instead of spread operator to avoid stack overflow
            let minLon = Infinity, maxLon = -Infinity;
            let minLat = Infinity, maxLat = -Infinity;

            coords.forEach(coord => {
                if (coord && coord.length >= 2) {
                    const lon = coord[0];
                    const lat = coord[1];
                    if (lon < minLon) minLon = lon;
                    if (lon > maxLon) maxLon = lon;
                    if (lat < minLat) minLat = lat;
                    if (lat > maxLat) maxLat = lat;
                }
            });

            bounds = { minLon, maxLon, minLat, maxLat };
        }
    }

    // Extract CRS/Projection
    let crs = 'WGS 84 (EPSG:4326)'; // Current projection (always WGS84 after import)
    let prj = null; // WKT string for .prj file
    let originalProjection = null; // Human-readable original projection name

    if (geojson.crs) {
        if (geojson.crs.properties && geojson.crs.properties.name) {
            crs = geojson.crs.properties.name;
        } else {
            crs = JSON.stringify(geojson.crs);
        }
    }

    // The raw .prj content is now attached to the normalized geojson
    if (geojson.prj) {
        prj = geojson.prj;

        // Extract projection name from WKT for display
        // Try PROJCS first (projected coordinate systems)
        let nameMatch = prj.match(/PROJCS\["([^"]+)"/);
        if (nameMatch) {
            originalProjection = nameMatch[1];
        } else {
            // Try GEOGCS (geographic coordinate systems)
            nameMatch = prj.match(/GEOGCS\["([^"]+)"/);
            if (nameMatch) {
                originalProjection = nameMatch[1];
            } else {
                // Fallback to custom projection
                originalProjection = 'Custom Projection';
            }
        }

        console.log('Using projection from .prj file:', originalProjection);
        console.log('WKT (first 100 chars):', prj.substring(0, 100));
    } else {
        console.log('No projection info found in shapefile');
    }

    // Get original bounds if available (from before reprojection)
    const originalBounds = geojson.originalBounds || null;

    return {
        featureCount,
        geometryTypes,
        propertyKeys,
        bounds, // Current bounds (WGS84)
        originalBounds, // Original bounds (before reprojection)
        crs, // Current projection (WGS84)
        prj, // Include the WKT projection string
        originalProjection // Human-readable original projection name
    };
}

/**
 * Extract all coordinates from a geometry
 * @param {Object} geometry - The geometry object
 * @param {Array} coords - Array to collect coordinates
 */
function extractCoordinates(geometry, coords) {
    if (!geometry) return;

    const { type, coordinates } = geometry;

    switch (type) {
        case 'Point':
            coords.push(coordinates);
            break;
        case 'MultiPoint':
        case 'LineString':
            coords.push(...coordinates);
            break;
        case 'MultiLineString':
        case 'Polygon':
            coordinates.forEach(ring => coords.push(...ring));
            break;
        case 'MultiPolygon':
            coordinates.forEach(polygon => {
                polygon.forEach(ring => coords.push(...ring));
            });
            break;
        case 'GeometryCollection':
            geometry.geometries.forEach(g => extractCoordinates(g, coords));
            break;
    }
}
